import React, { useEffect, useRef, useState } from "react";
import { normalizeTypeformUrl } from "@/lib/typeform";

declare global {
  interface Window {
    tf?: {
      createWidget: (
        formId: string,
        options: {
          container: HTMLElement;
          width?: number | string;
          height?: number | string;
          hideHeaders?: boolean;
          hideFooter?: boolean;
          opacity?: number;
          onSubmit?: () => void;
        }
      ) => { refresh: () => void; unmount: () => void };
    };
  }
}

const TYPEFORM_EMBED_SRC = "https://embed.typeform.com/next/embed.js";
const TYPEFORM_WIDGET_CSS = "https://embed.typeform.com/next/css/widget.css";

let embedScriptPromise: Promise<void> | null = null;

function loadTypeformWidgetCss(): void {
  if (typeof document === "undefined") return;
  if (document.querySelector(`link[href="${TYPEFORM_WIDGET_CSS}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = TYPEFORM_WIDGET_CSS;
  document.head.appendChild(link);
}

function loadTypeformEmbedScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.tf?.createWidget) return Promise.resolve();

  if (embedScriptPromise) return embedScriptPromise;
  embedScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TYPEFORM_EMBED_SRC}"]`
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("typeform_embed_script_load_failed")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = TYPEFORM_EMBED_SRC;
    script.async = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("typeform_embed_script_load_failed")), { once: true });
    document.body.appendChild(script);
  });
  return embedScriptPromise;
}

export function TypeformWidget({
  typeformUrl,
  onSubmit,
  className,
}: {
  typeformUrl: string;
  onSubmit?: () => void;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [canonicalUrl, setCanonicalUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    let watchdog: number | null = null;

    (async () => {
      try {
        setError(null);
        setCanonicalUrl(null);

        const normalized = normalizeTypeformUrl(typeformUrl);
        if (!normalized.ok) {
          throw new Error(`invalid_typeform_url:${normalized.reason}`);
        }
        setCanonicalUrl(normalized.url);

        loadTypeformWidgetCss();
        await loadTypeformEmbedScript();
        if (!active) return;
        const container = containerRef.current;
        if (!container) return;

        // Reset any previous widget in this container
        container.innerHTML = "";

        const createWidget = window.tf?.createWidget;
        if (!createWidget) throw new Error("typeform_embed_not_available");

        createWidget(normalized.formId, {
          container,
          width: "100%",
          height: "100%",
          hideHeaders: true,
          hideFooter: true,
          opacity: 0,
          onSubmit: () => {
            onSubmit?.();
          },
        });

        // If the embed library fails silently, we still want to surface a clear error.
        watchdog = window.setTimeout(() => {
          if (!active) return;
          const hasIframe = Boolean(container.querySelector("iframe"));
          if (!hasIframe) setError("typeform_embed_render_timeout");
        }, 3000);
      } catch (e) {
        if (!active) return;
        setError(e instanceof Error ? e.message : "typeform_embed_failed");
      }
    })();

    return () => {
      active = false;
      if (watchdog) window.clearTimeout(watchdog);
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [typeformUrl, onSubmit]);

  if (error) {
    const href = canonicalUrl ?? typeformUrl;
    return (
      <div className={className}>
        <div className="text-sm text-red-200">Failed to load assessment: {error}</div>
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-block text-sm text-primary underline underline-offset-2"
        >
          Open in a new tab
        </a>
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        ref={containerRef}
        data-typeform-container
        className="w-full h-[70vh] min-h-[400px] rounded-xl overflow-hidden border border-primary/20 bg-dark-slate"
      />
    </div>
  );
}


